import { BrandMark } from './components/BrandMark.tsx';
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { BellRing, CalendarDays, FileUp, Languages, Layers, Menu, Settings, Star, Sun } from 'lucide-react';
import type { DisplayItem, ISODate } from '@autoday/core';
import { useStore } from './store/appStore.ts';
import { useAuth } from './auth/authStore.ts';
import { navigate, useIsDesktop, useRoute, useToday, type Route } from './ui/hooks.ts';
import { toast, ToastRegion } from './ui/toast.tsx';
import { consumeNotice, noteFocusDate, type CalendarViewKind } from './ui/uiSession.ts';
import { TodayView } from './views/TodayView.tsx';
// FullCalendar laddas först när kalendervyn öppnas (mindre startpaket).
const CalendarView = lazy(() => import('./views/CalendarView.tsx').then((m) => ({ default: m.CalendarView })));
import { ImportantView } from './views/ImportantView.tsx';
import { CalendarsView } from './views/CalendarsView.tsx';
const ImportView = lazy(() => import('./views/ImportView.tsx').then((m) => ({ default: m.ImportView })));
import { SettingsView } from './views/SettingsView.tsx';
import { NotificationStatusView } from './views/NotificationStatusView.tsx';
import { ResetPasswordView } from './views/ResetPasswordView.tsx';
import { EventEditor, type EditorRequest } from './components/EventEditor.tsx';
import { ItemDetail } from './components/ItemDetail.tsx';
import { CopyDialog, type CopyRequest } from './components/CopyDialog.tsx';
import { FirstRun } from './components/FirstRun.tsx';
import { InAppReminders } from './components/InAppReminders.tsx';
import { ConflictBanner } from './components/ConflictBanner.tsx';
import { AccountBlock, AccountHeaderButton, AccountStatusBanner } from './components/account/AccountWidgets.tsx';
import { AuthDialog, type AuthMode } from './components/account/AuthDialog.tsx';
import { AccountSheet, LogoutFlow, useAccountNotices } from './components/account/AccountDialogs.tsx';
import { LanguageSwitch, PaletteSwitch, ThemeHeaderButton, ThemeSwitch } from './components/ThemeControls.tsx';
import { Sheet } from './ui/kit.tsx';
import type { EntryIntent } from './ui/entryPath.ts';
import { useTheme } from './ui/theme.ts';
import { PALETTES } from './ui/palettes.ts';
import { LANGS, useLang } from './i18n/i18n.ts';

export interface UiApi {
  openEditor: (r: EditorRequest) => void;
  /** intent 'prep' öppnar posten med "Hitta tid att förbereda" framme. */
  openItem: (item: DisplayItem | { ref: string; occurrenceKey?: string }, intent?: 'prep') => void;
  openCopy: (r: CopyRequest) => void;
  focusDate: ISODate;
  setFocusDate: (d: ISODate) => void;
  /** Kalendervy att återställa efter inloggning/utloggning (en gång). */
  initialCalendarView: CalendarViewKind | null;
  openAuth: (mode: AuthMode, prefillEmail?: string) => void;
  openAccount: () => void;
  startLogout: (opts?: { deleteLocal?: boolean }) => void;
}

const UiCtx = createContext<UiApi | null>(null);
export function useUi(): UiApi {
  const v = useContext(UiCtx);
  if (!v) throw new Error('UiCtx saknas');
  return v;
}

type NavItem = { route: Route['name']; sv: string; en: string; icon: ReactNode };
const NAV: NavItem[] = [
  { route: 'idag', sv: 'Idag', en: 'Today', icon: <Sun size={20} aria-hidden /> },
  { route: 'kalender', sv: 'Kalender', en: 'Calendar', icon: <CalendarDays size={20} aria-hidden /> },
  { route: 'viktigt', sv: 'Viktigt', en: 'Deadlines', icon: <Star size={20} aria-hidden /> },
  { route: 'kalendrar', sv: 'Mina kalendrar', en: 'My calendars', icon: <Layers size={20} aria-hidden /> },
  { route: 'import', sv: 'Import', en: 'Import', icon: <FileUp size={20} aria-hidden /> },
  { route: 'installningar', sv: 'Inställningar', en: 'Settings', icon: <Settings size={20} aria-hidden /> },
];
/** Notisstatus heter Påminnelsekoll i gränssnittet (adressen #/notiser är oförändrad). */
const GUARDIAN: NavItem = { route: 'notiser', sv: 'Påminnelsekoll', en: 'Reminder check', icon: <BellRing size={20} aria-hidden /> };
const MOBILE_NAV: Route['name'][] = ['idag', 'kalender', 'viktigt', 'import', 'installningar'];

export function App({ initialFocusDate, initialCalendarView, entryIntent = null }: { initialFocusDate: ISODate | null; initialCalendarView: CalendarViewKind | null; entryIntent?: EntryIntent }) {
  const snap = useStore();
  const auth = useAuth();
  const route = useRoute();
  const desktop = useIsDesktop();
  const today = useToday();
  // Hela trädet ritas om vid språkbyte (inga memo-komponenter); text hämtas med t()/tr().
  const { t } = useLang();
  const [editor, setEditor] = useState<EditorRequest | null>(null);
  const [selected, setSelected] = useState<{ ref: string; occurrenceKey?: string; intent?: 'prep' } | null>(null);
  const [copy, setCopy] = useState<CopyRequest | null>(null);
  const [focusDate, setFocusDate] = useState<ISODate>(initialFocusDate ?? today);
  const [authDialog, setAuthDialog] = useState<{ mode: AuthMode; email?: string } | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [logout, setLogout] = useState<{ deleteLocal: boolean } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  useAccountNotices();

  // En gissad adress som /logout loggar aldrig ut av sig själv: visa den vanliga vägen i stället.
  const [entryHandled, setEntryHandled] = useState(entryIntent === null);
  useEffect(() => {
    if (entryHandled || !snap.ready) return;
    setEntryHandled(true);
    if (auth.state.status === 'guest') {
      toast({
        kind: 'info',
        text: t(
          'Adressen /logout loggar inte ut någon. Du använder AutoDay utan konto på den här enheten, så det finns inget att logga ut från.',
          'The address /logout does not sign anyone out. You are using AutoDay without an account on this device, so there is nothing to sign out of.',
        ),
        timeout: 12000,
      });
    } else {
      setAccountOpen(true);
      toast({ kind: 'info', text: t('Adressen /logout loggar inte ut av sig själv. Välj Logga ut i kontorutan om du vill logga ut.', 'The address /logout does not sign you out by itself. Choose Sign out in the account panel if you want to sign out.'), timeout: 12000 });
    }
  }, [entryHandled, snap.ready, auth.state.status, t]);

  useEffect(() => noteFocusDate(focusDate), [focusDate]);
  useEffect(() => {
    const n = consumeNotice();
    if (n) toast({ kind: 'info', text: n, timeout: 9000 });
  }, []);

  const openItem = useCallback((i: DisplayItem | { ref: string; occurrenceKey?: string }, intent?: 'prep') => {
    if ('uid' in i) setSelected({ ref: i.ref.kind === 'local' ? i.ref.id : i.ref.key, occurrenceKey: i.occurrenceKey, ...(intent ? { intent } : {}) });
    else setSelected({ ...i, ...(intent ? { intent } : {}) });
  }, []);
  const ui = useMemo<UiApi>(
    () => ({
      openEditor: setEditor,
      openItem,
      openCopy: setCopy,
      focusDate,
      setFocusDate,
      initialCalendarView,
      openAuth: (mode, prefillEmail) => {
        setAccountOpen(false);
        setAuthDialog({ mode, ...(prefillEmail ? { email: prefillEmail } : {}) });
      },
      openAccount: () => setAccountOpen(true),
      startLogout: (opts) => {
        setAccountOpen(false);
        setLogout({ deleteLocal: !!opts?.deleteLocal });
      },
    }),
    [openItem, focusDate, initialCalendarView],
  );

  if (!snap.ready) {
    return (
      <div className="grid h-full place-items-center text-muted" role="status">
        {t('Laddar AutoDay…', 'Loading AutoDay…')}
      </div>
    );
  }
  if (snap.loadError) {
    return (
      <div className="mx-auto max-w-lg p-6" role="alert">
        <h1 className="text-lg font-semibold">{t('AutoDay kunde inte öppna den lokala databasen', 'AutoDay could not open the local database')}</h1>
        <p className="mt-2 text-sm text-muted">{snap.loadError}</p>
        <p className="mt-2 text-sm text-muted">
          {t(
            'Ingen data har raderats. Prova att ladda om sidan. Om felet kvarstår: kontrollera att webbläsaren tillåter lagring för denna sida (privat läge kan blockera IndexedDB).',
            'No data has been deleted. Try reloading the page. If the problem remains, check that the browser allows storage for this site (private mode can block IndexedDB).',
          )}
        </p>
      </div>
    );
  }
  if (auth.switching && !logout) {
    return (
      <div className="grid h-full place-items-center text-muted" role="status">
        {t('Byter profil…', 'Switching profile…')}
      </div>
    );
  }

  const postRoute = route.name === 'post' ? route : null;
  const activeSelected: { ref: string; occurrenceKey?: string; intent?: 'prep' } | null = postRoute ? { ref: postRoute.ref } : selected;
  const closeDetail = () => {
    setSelected(null);
    if (postRoute) navigate('idag');
  };
  const navLink = (n: NavItem) => (
    <a
      key={n.route}
      href={`#/${n.route}`}
      aria-current={route.name === n.route ? 'page' : undefined}
      className={`flex min-h-11 shrink-0 items-center gap-3 rounded-lg px-3 text-sm ${route.name === n.route ? 'bg-raised font-semibold text-text' : 'text-muted hover:bg-raised hover:text-text'}`}
    >
      {n.icon}
      {t(n.sv, n.en)}
    </a>
  );

  return (
    <UiCtx.Provider value={ui}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-lg focus:bg-accent focus:px-3 focus:py-2 focus:text-on-accent">
        {t('Hoppa till innehåll', 'Skip to content')}
      </a>
      <div className="autoday-shell flex h-full min-h-0">
        {desktop && (
          <nav aria-label={t('Huvudmeny', 'Main menu')} className="autoday-sidebar flex w-56 shrink-0 flex-col overflow-y-auto border-r border-border bg-panel">
            <div className="flex items-center gap-2 px-5 py-4">
              <BrandMark size={34} />
              <span className="autoday-wordmark text-lg font-semibold tracking-tight">AutoDay</span>
            </div>
            {/* Menyn skrollar vid låg fönsterhöjd – kontoblocket nedan förblir synligt. */}
            <div className="flex min-h-24 flex-1 flex-col gap-1 overflow-y-auto px-3">{NAV.map(navLink)}</div>
            <div className="shrink-0 border-t border-border px-3 pb-3 pt-2">
              <div className="mb-1 px-1">
                <ThemeSwitch size="compact" hideLegend />
              </div>
              <AppearanceButton onOpen={() => setAppearanceOpen(true)} />
              {navLink(GUARDIAN)}
              <div className="mt-3">
                <AccountBlock />
              </div>
            </div>
          </nav>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          {!desktop && (
            <header className="autoday-mobile-header relative flex shrink-0 items-center gap-1 border-b border-border bg-panel px-2 py-1.5">
              <button
                type="button"
                className="grid min-h-11 min-w-11 place-items-center rounded-lg text-text hover:bg-raised"
                aria-label={t('Meny', 'Menu')}
                title={t('Alla vyer, utseende och konto', 'All views, appearance and account')}
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(true)}
              >
                <Menu size={22} aria-hidden />
              </button>
              <BrandMark size={30} />
              <span className="autoday-wordmark min-w-0 flex-1 truncate font-semibold tracking-tight">AutoDay</span>
              <ThemeHeaderButton />
              <AccountHeaderButton />
            </header>
          )}
          <AccountStatusBanner />
          <ConflictBanner />
          <main id="main" className="min-h-0 flex-1 overflow-y-auto" tabIndex={-1}>
            {route.name === 'idag' && <TodayView />}
            {route.name === 'kalender' && (
              <Suspense fallback={<p className="p-6 text-sm text-muted" role="status">{t('Laddar kalender…', 'Loading calendar…')}</p>}>
                <CalendarView />
              </Suspense>
            )}
            {route.name === 'viktigt' && <ImportantView />}
            {route.name === 'kalendrar' && <CalendarsView />}
            {route.name === 'import' && (
              <Suspense fallback={<p className="p-6 text-sm text-muted" role="status">{t('Laddar import…', 'Loading import…')}</p>}>
                <ImportView />
              </Suspense>
            )}
            {route.name === 'installningar' && <SettingsView />}
            {route.name === 'notiser' && <NotificationStatusView />}
            {route.name === 'aterstall' && <ResetPasswordView />}
            {route.name === 'post' && <TodayView />}
          </main>
          {!desktop && (
            <nav aria-label={t('Huvudmeny', 'Main menu')} className="autoday-mobile-nav autoday-safe-bottom grid shrink-0 grid-cols-5 border-t border-border bg-panel pt-1">
              {MOBILE_NAV.map((r) => {
                const n = NAV.find((x) => x.route === r)!;
                return (
                  <a
                    key={r}
                    href={`#/${r}`}
                    aria-current={route.name === r ? 'page' : undefined}
                    className={`flex min-h-12 min-w-0 flex-col items-center justify-center gap-0.5 text-[11px] ${route.name === r ? 'font-semibold text-accent' : 'text-muted'}`}
                  >
                    {n.icon}
                    <span className="max-w-full truncate px-0.5">{t(n.sv, n.en)}</span>
                  </a>
                );
              })}
            </nav>
          )}
        </div>
      </div>
      {editor && <EventEditor request={editor} onClose={() => setEditor(null)} />}
      {activeSelected && <ItemDetail key={activeSelected.ref + (activeSelected.intent ?? '')} refId={activeSelected.ref} occurrenceKey={activeSelected.occurrenceKey} intent={activeSelected.intent} onClose={closeDetail} />}
      {copy && <CopyDialog request={copy} onClose={() => setCopy(null)} />}
      {authDialog && <AuthDialog initialMode={authDialog.mode} prefillEmail={authDialog.email} onClose={() => setAuthDialog(null)} />}
      {accountOpen && <AccountSheet onClose={() => setAccountOpen(false)} onLogout={(o) => ui.startLogout(o)} />}
      {logout && <LogoutFlow deleteLocal={logout.deleteLocal} onDone={() => setLogout(null)} />}
      {menuOpen && <MobileMenu route={route.name} onClose={() => setMenuOpen(false)} />}
      {appearanceOpen && <AppearanceSheet onClose={() => setAppearanceOpen(false)} />}
      {!snap.device.firstRunDone && !authDialog && route.name !== 'aterstall' && <FirstRun />}
      <InAppReminders />
      <ToastRegion />
    </UiCtx.Provider>
  );
}

/** Sidomenyn: aktuell palett och språk på en rad; öppnar Utseende och språk. */
function AppearanceButton({ onOpen }: { onOpen: () => void }) {
  const { palette } = useTheme();
  const { t, lang } = useLang();
  const langLabel = LANGS.find((l) => l.value === lang)!.label;
  return (
    <button type="button" onClick={onOpen} aria-haspopup="dialog" className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm text-muted hover:bg-raised hover:text-text">
      <Languages size={20} aria-hidden className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        <span className="sr-only">{t('Utseende och språk: ', 'Appearance and language: ')}</span>
        {PALETTES[palette].name[lang]} · {langLabel}
      </span>
    </button>
  );
}

function AppearanceSheet({ onClose }: { onClose: () => void }) {
  const { t } = useLang();
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={t('Utseende och språk', 'Appearance and language')}>
      <div className="grid gap-5">
        <ThemeSwitch />
        <PaletteSwitch />
        <LanguageSwitch />
        <p className="text-xs text-muted">
          {t('Valen sparas bara på den här enheten och påverkar inte kalenderdata, tider eller påminnelser.', 'These choices are saved on this device only and do not change calendar data, times or reminders.')}
        </p>
      </div>
    </Sheet>
  );
}

/**
 * Mobil och smala fönster (under 1024 px, även 200 % zoom): samma vägar som datorns sidomeny – alla vyer,
 * Påminnelsekoll, tema, palett, språk och konto – i ett ark från sidhuvudet. Bottenraden har bara de vanligaste vyerna.
 */
function MobileMenu({ route, onClose }: { route: Route['name']; onClose: () => void }) {
  const { t } = useLang();
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={t('Meny', 'Menu')}>
      <nav aria-label={t('Alla vyer', 'All views')} className="grid gap-1">
        {[...NAV, GUARDIAN].map((n) => (
          <a
            key={n.route}
            href={`#/${n.route}`}
            onClick={onClose}
            aria-current={route === n.route ? 'page' : undefined}
            className={`flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm ${route === n.route ? 'bg-accent-soft font-semibold text-accent' : 'text-text hover:bg-raised'}`}
          >
            {n.icon}
            {t(n.sv, n.en)}
          </a>
        ))}
      </nav>
      <div className="mt-4 grid gap-4 border-t border-border pt-4">
        <ThemeSwitch />
        <PaletteSwitch size="compact" />
        <LanguageSwitch />
        <AccountBlock variant="menu" onAction={onClose} />
      </div>
    </Sheet>
  );
}
